// The Cycle Map: the form-cycle feature as the dynamical system it secretly is.
// "Which voicing does the next pass start on?" is a function on a finite set —
// draw every node and arrow and you can SEE why Cycle form closes (the orbit
// returns to its start), caps (it falls into a cycle that excludes the start),
// or does nothing (a fixed point). Reads whatever chart is loaded in the
// Workbench.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { APP_ROUTES } from '@/navigation';
import { chartToSequence } from '@/music/chart';
import { formCycleMap, type CycleMapNode } from '@/music/voiceLeading';
import { voicingIdentity, voicingIdentityLabel } from '@/music/voicing';
import { getChordPlayer } from '@/audio/player';
import { useStore } from '@/state/store';

const SIZE = 400;
const C = SIZE / 2;
const R = 138;

function nodeXY(i: number, count: number): [number, number] {
  const a = ((90 - (i * 360) / count) * Math.PI) / 180;
  return [C + R * Math.cos(a), C - R * Math.sin(a)];
}
function lowFret(node: CycleMapNode): string {
  const frets = node.fingering.frets.filter(
    (f): f is number => f !== null && f !== undefined && f > 0,
  );
  return frets.length ? `fr ${Math.min(...frets)}` : 'open';
}
function registerLabel(stringSet: CycleMapNode['stringSet']): string {
  return stringSet === 'middle' ? 'Lower' : 'Upper';
}

/** Follow the orbit from a start node until any node repeats (the ρ shape). */
function orbit(map: CycleMapNode[], start: number) {
  const seen = [start];
  let at = start;
  let closed = false;
  for (let k = 1; k <= map.length + 1; k++) {
    at = map[at].next;
    if (at < 0) break;
    const j = seen.indexOf(at);
    if (j >= 0) {
      closed = j === 0;
      return { steps: seen, closed, entry: j };
    }
    seen.push(at);
  }
  return { steps: seen, closed, entry: 0 };
}

export function CycleMapExhibit() {
  const chart = useStore((s) => s.chart);
  const stringSet = useStore((s) => s.stringSet);
  const voicingFamily = useStore((s) => s.voicingFamily);
  const avoidB9 = useStore((s) => s.avoidB9);
  const freeStringSet = useStore((s) => s.freeStringSet);
  const startingInversion = useStore((s) => s.startingInversion);
  const setStartingInversion = useStore((s) => s.setStartingInversion);
  const [picked, setPicked] = useState<number | null>(null);

  const sequence = useMemo(() => chartToSequence(chart, { stringSet }), [chart, stringSet]);
  const map = useMemo(() => {
    if (sequence.length < 2) return null;
    try {
      return formCycleMap(sequence, { avoidB9, freeStringSet, voicingFamily });
    } catch {
      return null;
    }
  }, [sequence, avoidB9, freeStringSet, voicingFamily]);

  if (!map) {
    return (
      <div className="lab-exhibit" data-testid="lab-cycle-map">
        <p className="lab-lede">
          This map needs a tune. Open one with at least two chords on the{' '}
          <Link to={APP_ROUTES.workbench}>workbench</Link> first.
        </p>
      </div>
    );
  }

  // The grip Cycle form opens on for an opening top voice: the first node on
  // the home set at that inversion (drop 2 before drop 3, the search's order).
  const openingFor = (inversion: number) =>
    map.findIndex((n) => n.stringSet === stringSet && n.inversion === inversion);
  const startIdx = (() => {
    if (picked !== null && picked < map.length) return picked;
    const home = openingFor(startingInversion);
    return home >= 0 ? home : 0;
  })();
  const o = orbit(map, startIdx);
  const cycleNodes = new Set(o.steps.slice(o.entry));
  const passes = o.steps.length;
  const reentry = map[o.steps[o.steps.length - 1]].next;

  const first = sequence[0];
  const identity = (n: CycleMapNode) => voicingIdentity(n.fingering, first.root, first.chordType);
  const identityLabel = (n: CycleMapNode) =>
    voicingIdentityLabel(n.fingering, first.root, first.chordType);
  const topShort = (n: CycleMapNode) => {
    const top = identity(n).topTone;
    return top ? `${top} top` : '— top';
  };
  const bassShort = (n: CycleMapNode) => {
    const bass = identity(n).bassTone;
    return bass ? `${bass} bass` : '— bass';
  };

  const edgePath = (i: number, j: number): string => {
    const [x1, y1] = nodeXY(i, map.length);
    if (i === j) {
      // Fixed point: a small self-loop drawn outward from the node.
      const dx = x1 - C;
      const dy = y1 - C;
      const len = Math.hypot(dx, dy) || 1;
      const ox = (dx / len) * 34;
      const oy = (dy / len) * 34;
      return `M ${x1 + ox * 0.45} ${y1 + oy * 0.45} A 15 15 0 1 1 ${x1 + ox * 0.45 + 1} ${y1 + oy * 0.45 + 1}`;
    }
    const [x2, y2] = nodeXY(j, map.length);
    // Curve toward the centre so opposite arrows don't overlap.
    const mx = (x1 + x2) / 2 + (C - (x1 + x2) / 2) * 0.35;
    const my = (y1 + y2) / 2 + (C - (y1 + y2) / 2) * 0.35;
    return `M ${x1} ${y1} Q ${mx} ${my} ${x2} ${y2}`;
  };

  const onOrbit = (i: number) => {
    const at = o.steps.indexOf(i);
    return at >= 0 && at < o.steps.length;
  };

  return (
    <div className="lab-exhibit" data-testid="lab-cycle-map">
      <p className="lab-lede">
        <strong>Cycle form</strong> on the workbench repeats one step: given the voicing a pass
        starts on, find the voicing the <em>next</em> pass starts on. There are finitely many
        voicings, so every sequence of passes ends in a cycle. This is the map for{' '}
        <strong>{first.symbol}</strong>, the first chord of{' '}
        <em>{chart.title || 'the loaded tune'}</em>. Choose a node to start there and hear it.
      </p>

      <div className="lab-row">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          style={{ width: '100%', maxWidth: 420 }}
          role="group"
          aria-label="Interactive cycle map"
        >
          <defs>
            <marker
              id="cm-arrow"
              viewBox="0 0 8 8"
              refX={7}
              refY={4}
              markerWidth={7}
              markerHeight={7}
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--muted)" />
            </marker>
            <marker
              id="cm-arrow-hot"
              viewBox="0 0 8 8"
              refX={7}
              refY={4}
              markerWidth={7}
              markerHeight={7}
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 8 4 L 0 8 z" fill="var(--accent)" />
            </marker>
          </defs>
          {map.map((n, i) => {
            const isOrbitEdge = onOrbit(i); // this node's outgoing arrow is walked
            return (
              <path
                key={`e${i}`}
                d={edgePath(i, n.next)}
                fill="none"
                stroke={isOrbitEdge ? 'var(--accent)' : 'var(--hairline)'}
                strokeWidth={isOrbitEdge ? 2.4 : 1.4}
                markerEnd={isOrbitEdge ? 'url(#cm-arrow-hot)' : 'url(#cm-arrow)'}
              />
            );
          })}
          {map.map((n, i) => {
            const [x, y] = nodeXY(i, map.length);
            const isStart = i === startIdx;
            const inCycle = cycleNodes.has(i);
            const activate = () => {
              setPicked(i);
              // The control names an inversion, not a family: only the node it
              // would open on may set it, or a drop 3 twin starts Cycle form on
              // its drop 2 grip.
              if (openingFor(n.inversion) === i) setStartingInversion(n.inversion);
              void getChordPlayer().playFingering(n.fingering, n.stringSet);
            };
            return (
              <g
                key={`${n.stringSet}:${n.fingering.voicingFamily}:${n.inversion}`}
                data-testid={`cm-node-${i}`}
                role="button"
                tabIndex={0}
                aria-label={`${identityLabel(n)}, ${registerLabel(n.stringSet)}, ${lowFret(n)}`}
                onClick={activate}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    activate();
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                {inCycle && (
                  <circle
                    cx={x}
                    cy={y}
                    r={33}
                    fill="none"
                    stroke="var(--accent)"
                    strokeDasharray="4 3"
                  />
                )}
                <circle
                  cx={x}
                  cy={y}
                  r={28}
                  fill={isStart ? 'var(--accent)' : 'var(--surface)'}
                  stroke={isStart ? 'var(--accent)' : 'var(--hairline)'}
                  strokeWidth={1.4}
                />
                <text
                  x={x}
                  y={y - 8}
                  textAnchor="middle"
                  fontSize={9}
                  fontWeight={750}
                  fill={isStart ? 'var(--accent-ink)' : 'var(--ink)'}
                >
                  {topShort(n)}
                </text>
                <text
                  x={x}
                  y={y + 3}
                  textAnchor="middle"
                  fontSize={8.5}
                  fill={isStart ? 'var(--accent-ink)' : 'var(--muted)'}
                >
                  {bassShort(n)}
                </text>
                <text
                  x={x}
                  y={y + 14}
                  textAnchor="middle"
                  fontSize={8}
                  fill={isStart ? 'var(--accent-ink)' : 'var(--muted)'}
                >
                  {freeStringSet ? `${registerLabel(n.stringSet)} · ` : ''}
                  {lowFret(n)}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="lab-panel">
          <div className="lab-panel-title" data-testid="cm-verdict">
            {passes === 1 && o.closed
              ? 'Fixed point: the next pass starts on the same voicing.'
              : o.closed
                ? `Returns to this voicing after ${passes} passes.`
                : `Enters a cycle after ${o.entry} pass${o.entry === 1 ? '' : 'es'} and does not return to this voicing (Cycle form stops at 4 passes).`}
          </div>
          <p className="muted">
            Orbit:{' '}
            {o.steps.map((s, i) => (
              <span key={i}>
                {i > 0 && ' → '}
                <strong>{identityLabel(map[s])}</strong>
              </span>
            ))}
            {o.closed ? ' → (start)' : ` → (${identityLabel(map[reentry] ?? map[0])} again)`}
          </p>
          <p className="muted">
            Dashed rings mark the cycle the passes end in. With {map.length} possible opening
            voicings, a voicing must repeat within {map.length} passes; Cycle form stops at 4.
            Clicking a node in the {registerLabel(stringSet).toLowerCase()} register also sets the
            opening top voice.
          </p>
        </div>
      </div>
    </div>
  );
}
